import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { ShieldCheck, Stethoscope, Handshake, Sparkles, Target, FileText, Radio, Database, Zap } from 'lucide-react';
interface SlideAIEconomicsProps {
  onContinue: () => void;
}
const CARDS = [{
  icon: ShieldCheck,
  title: 'Deep pharma marketing expertise',
  desc: 'Three decades operating inside regulated pharma sales and marketing — where generalist AI and broad agencies fail.'
}, {
  icon: Stethoscope,
  title: '100% focused on Healthcare',
  desc: 'No side bets, no other verticals — every workflow, model and template is purpose-built for HCP audiences.'
}, {
  icon: Handshake,
  title: 'Trusted by leading pharma',
  desc: 'Master agreements with 15 of the top 30 healthcare companies globally.'
}, {
  icon: Sparkles,
  title: 'Interactive engagement',
  desc: 'Every engagement is a lesson learned — behavioral signals continuously sharpen the next campaign.'
}, {
  icon: Target,
  title: 'One-to-one precision',
  desc: "Tailored delivery to each HCP segment — not agencies' broad-stroke campaigns."
}, {
  icon: FileText,
  title: 'Content agnostic',
  desc: 'We work with your existing approved assets — no rip-and-replace, no new content factory required.'
}, {
  icon: Radio,
  title: 'Publishing agnostic',
  desc: 'Distribute everywhere HCPs are — email, banners, WhatsApp, SMS, Veeva, portals, sales reps, congresses. We integrate, you choose.'
}, {
  icon: Database,
  title: 'Actionable data layer',
  desc: 'Rich behavioral signals from polls, quizzes, rankings and interactions — use them standalone or stream them into any analytics platform (Veeva, Tableau, Power BI, your CDP). Data agnostic by design.'
}, {
  icon: Zap,
  title: 'Focused AI × Pharma expertise × Compounding flywheel',
  desc: 'Purpose-built AI shaped by deep pharma knowledge, that gets sharper with every campaign. The combination is what no agency or generalist AI can replicate.'
}];
export function SlideAIEconomics({
  onContinue
}: SlideAIEconomicsProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 200);
    const t2 = setTimeout(() => setStage(2), 600);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);
  return <div className="h-full w-full flex flex-col bg-white px-8 py-7 md:px-16 md:py-9 max-w-[1400px] mx-auto overflow-y-auto" data-id="element-154" data-mp="1dqmni7drahuj">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      y: stage >= 1 ? 0 : 20
    }} transition={{
      duration: 0.5
    }} data-id="element-155" data-mp="1nnhm0qa14i8t">
        <p className="text-[#6C2BD9] text-xs md:text-sm font-bold uppercase tracking-[0.2em]" data-id="element-156" data-mp="uyabesxaw277">
          Why Snakker, why now
        </p>
        <h2 className="text-3xl md:text-5xl font-sans font-bold text-slate-900 tracking-tight leading-tight mt-2" data-id="element-157" data-mp="se4ayv7watve">
          Snakker was born to{' '}
          <span className="text-[#6C2BD9]" data-id="element-158" data-mp="nfkl4jq6kxxr">solve this</span>
        </h2>
        <p className="text-slate-600 text-base md:text-lg mt-2 max-w-3xl" data-id="element-159" data-mp="if57rs5wfqfs">
          Unlocking the legacy long-tail requires a very specific combination of
          pharma fluency, AI, distribution and a closed feedback loop — and
          Snakker is the only platform that brings all of it together.
        </p>
      </motion.div>

      {/* 3x3 grid */}
      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mt-8" data-id="element-160" data-mp="4mp2ekpizgbp">
        {CARDS.map((c, idx) => {
        const Icon = c.icon;
        return <motion.div key={c.title} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: stage >= 2 ? 1 : 0,
          y: stage >= 2 ? 0 : 20
        }} transition={{
          duration: 0.4,
          delay: 0.05 * idx
        }} className="rounded-2xl bg-white border border-slate-200 p-5 md:p-6 flex flex-col" data-id="element-161" data-mp="3qxb52gu9568b" data-mpi={idx}>
              <div className="w-10 h-10 rounded-xl bg-[#F5F0FF] border border-[#E4D7FF] flex items-center justify-center mb-4" data-id="element-162" data-mp="1n8bm271xdyx">
                <Icon className="w-5 h-5 text-[#6C2BD9]" data-id="element-163" data-mp="5nimnt9paukn" />
              </div>
              <h3 className="text-base md:text-lg font-bold text-slate-900 leading-snug" data-id="element-164" data-mp="3ntn73unpuf8l">
                {c.title}
              </h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed" data-id="element-165" data-mp="3eujlok12tnqx">
                {c.desc}
              </p>
            </motion.div>;
      })}
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 2 ? 1 : 0
    }} transition={{
      duration: 0.5,
      delay: 0.6
    }} className="mt-6 flex justify-end" data-id="element-166" data-mp="3iwewcf4wddhd"></motion.div>
    </div>;
}